import { auth } from '../config/firebase';

async function getAuthHeaders(): Promise<HeadersInit> {
  const headers: Record<string, string> = {
    'Content-Type': 'application/json',
  };

  try {
    const user = auth.currentUser;
    if (user) {
      const token = await user.getIdToken();
      headers['Authorization'] = `Bearer ${token}`;
      if (user.email) {
        headers['x-user-email'] = user.email;
      }
    }
  } catch (err) {
    console.warn('[Admin API Service] Error fetching token:', err);
  }

  return headers;
}

export const adminApiService = {
  async getOverview() {
    const headers = await getAuthHeaders();
    const res = await fetch('/api/admin/overview', { headers });
    return res.json();
  },

  async getUsers(params?: { search?: string; status?: string; page?: number; limit?: number }) {
    const headers = await getAuthHeaders();
    const query = new URLSearchParams();
    if (params?.search) query.set('search', params.search);
    if (params?.status) query.set('status', params.status);
    if (params?.page) query.set('page', String(params.page));
    if (params?.limit) query.set('limit', String(params.limit));

    const res = await fetch(`/api/admin/users?${query.toString()}`, { headers });
    return res.json();
  },

  async getTenants() {
    const headers = await getAuthHeaders();
    const res = await fetch('/api/admin/tenants', { headers });
    return res.json();
  },

  async getWebsites() {
    const headers = await getAuthHeaders();
    const res = await fetch('/api/admin/websites', { headers });
    return res.json();
  },

  async getPayments() {
    const headers = await getAuthHeaders();
    const res = await fetch('/api/admin/payments', { headers });
    return res.json();
  },

  async getOrders() {
    const headers = await getAuthHeaders();
    const res = await fetch('/api/admin/orders', { headers });
    return res.json();
  },

  async getSubscriptions() {
    const headers = await getAuthHeaders();
    const res = await fetch('/api/admin/subscriptions', { headers });
    return res.json();
  },

  async getUpcomingPayments() {
    const headers = await getAuthHeaders();
    const res = await fetch('/api/admin/upcoming-payments', { headers });
    return res.json();
  },

  async getExternalLicenses() {
    const headers = await getAuthHeaders();
    const res = await fetch('/api/admin/external-licenses', { headers });
    return res.json();
  },

  async approveExternalLicense(id: string) {
    const headers = await getAuthHeaders();
    const res = await fetch(`/api/admin/external-licenses/${id}/approve`, {
      method: 'PATCH',
      headers,
    });
    return res.json();
  },

  async updateExternalLicenseStatus(id: string, status: string, notes?: string) {
    const headers = await getAuthHeaders();
    const res = await fetch(`/api/admin/external-licenses/${id}/status`, {
      method: 'PATCH',
      headers,
      body: JSON.stringify({ status, notes }),
    });
    return res.json();
  },

  async getDomains() {
    const headers = await getAuthHeaders();
    const res = await fetch('/api/admin/domains', { headers });
    return res.json();
  },

  async getMessages() {
    const headers = await getAuthHeaders();
    const res = await fetch('/api/admin/messages', { headers });
    return res.json();
  },

  async getTrials() {
    const headers = await getAuthHeaders();
    const res = await fetch('/api/admin/trials', { headers });
    return res.json();
  },

  async getSystemHealth() {
    const headers = await getAuthHeaders();
    const res = await fetch('/api/admin/system', { headers });
    return res.json();
  },

  async getSettings() {
    const headers = await getAuthHeaders();
    const res = await fetch('/api/admin/settings', { headers });
    return res.json();
  },

  async saveSettings(settings: any) {
    const headers = await getAuthHeaders();
    const res = await fetch('/api/admin/settings', {
      method: 'POST',
      headers,
      body: JSON.stringify(settings),
    });
    return res.json();
  },

  async getLogs() {
    const headers = await getAuthHeaders();
    const res = await fetch('/api/admin/logs', { headers });
    return res.json();
  },

  async performAction(payload: {
    actionType: string;
    targetType: string;
    targetId: string;
    details?: string;
    newValue?: any;
  }) {
    const headers = await getAuthHeaders();
    const res = await fetch('/api/admin/actions', {
      method: 'POST',
      headers,
      body: JSON.stringify(payload),
    });
    return res.json();
  },
};
